import React, { useEffect, Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import ProtectedRoute from "./ProtectedRoute";

// ✅ GTM pageview tracker — moved to top with other imports
import GtmRouteChangeTracker from "./GtmRouteChangeTracker";

// 🎨 Global Styles
import GlobalStyleDashboard from "./globalStyles";
import GlobalStylePublic from "./styles/GlobalStyle";

// 📄 Public Pages
import Home from "./pages/Home";
import DlaKogo from "./pages/DlaKogo";
import Cennik from "./pages/Cennik";
import Contact from "./pages/Contact";
import SelectLicensePage from "./pages/SelectLicensePage";
import OrderSummaryPage from "./pages/OrderSummaryPage";
import SuccessPage from "./pages/SuccesPage";
import CancelPage from "./pages/CancelPage";
import Blog from "./pages/Blog";   


// 🔐 Auth Pages
import Login from "./Login";
import Register from "./Register";
import RegisterDemo from "./RegisterDemo.js";
import ConfirmEmail from "./components/ConfirmEmail";
import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";
import DemoAutoLogin from "./DemoAutoLogin";

// ADMIN PANEL
import AdminLogin from "./components/adminpanel/AdminLogin";
import AdminPanel from "./components/adminpanel/AdminPanel";
import AdminUserList from "./components/adminpanel/AdminUsers";
import AdminOrdersList from "./components/adminpanel/AdminOrdersList";
import Przychody from "./components/adminpanel/Przychody";
import AdminInvoicesList from "./components/adminpanel/AdminInvoicesList";
import Settings from "./components/adminpanel/Settings";
import AdminBasicDemoAccesses from "./components/adminpanel/AdminBasicDemoAccesses";

// 📊 Dashboard & Internal
import DailySales from "./components/DailySales";
import ImportComponent from "./components/import/ImportComponent";
import RaportONasPage from "./components/RaportONasPage";
import SprzedazComponent from "./components/sprzedaz/SprzedazComponent";
import SprzedazDzien from "./components/sprzedaz/SprzedazDzien";
import SprzedazMiec from "./components/sprzedaz/SpzedazMiec";
import SprzedazData from "./components/sprzedaz/SprzedazData";
import SprzedazCompare from "./components/sprzedaz/SprzedazCompare";
import SprzedazTypow from "./components/sprzedaz/SprzedazTypow";
import SprzedazMap from "./components/sprzedaz/SprzedazMap";
import SprzedazCreative from "./components/sprzedaz/SprzedazCreative";

import ZakupyDzien from "./components/zakupy/ZakupyDzien";
import ZakupyKontrahent from "./components/zakupy/ZakupyKontrahent";
import ZakupyRegion from "./components/zakupy/ZakupyRegion";
import ZakupyTypow from "./components/zakupy/ZakupyTypow";
import ZakupyCompare from "./components/zakupy/ZakupyCompare";
import ZakupyCreative from "./components/zakupy/ZakupyCreative";
import ZakupyData from "./components/zakupy/ZakupyData";


import KlienciComponent from "./components/kontrahent/KlienciComponent";
import Faktura from "./components/admin/Faktura";
import InvoicingSettings from "./components/admin/InvoicingSettings";
import Subscription from "./components/admin/Subscription.js";
import NotFound from "./pages/NotFound";
import ImportOverview from "./components/import/ImportOverview.jsx";
import ImportHistory from "./components/import/ImportHistory.jsx";
import FinancialAnalysisPage from "./components/financial/FinancialAnalysisPage.jsx";
import ImportLicenseGuard from "./components/import/ImportLicenseGuard.jsx";



// 🔎 Lazy pages (must come AFTER imports)
const RaportWiarygodnosciPage = React.lazy(() =>
  import("./components/api/transparentdata/kontreport/RaportWiarygodnosciPage.jsx")
);
const KrsPelnyPage = React.lazy(() => import("./components/api/krs/KrsPelnyPage.jsx"));

const KrsAktualnyPage = React.lazy(() => import("./components/api/krs/KrsAktualnyPage.jsx"));

const CeodgAktualnyPage = React.lazy(() =>
  import("./components/api/ceodg/CeodgAktualnyPage.jsx")
);

// 🧠 Global style selector
const GlobalStylesSelector = () => {
  const location = useLocation();

  const publicStyleRoutes = ["/", "/partner", "/cennik", "/contact", "/blog", "/demo-login"];

  const authStyleRoutes = [
    "/login",
    "/register",
    "/register-demo",
    "/forgot-password",
    "/reset-password",
    "/confirm-email",
    "/adminpanel/adminlogin",
  ];

  const usePublicStyle = publicStyleRoutes.includes(location.pathname);
  const useAuthStyle = authStyleRoutes.includes(location.pathname);

  return usePublicStyle ? (
    <GlobalStylePublic />
  ) : useAuthStyle ? (
    <GlobalStyleDashboard />
  ) : (
    <GlobalStyleDashboard />
  );
};

// 💾 Persist ?token= from URL to localStorage
const TokenPersister = () => {
  const { search } = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(search);
    const t = params.get("token");
    if (t) localStorage.setItem("token", t);
  }, [search]);
  return null;
};

// 🔐 Session watcher (inactivity logout)
const SessionWatcher = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let logoutTimer;

    // Protected base paths
    const protectedPaths = [
      "/dashboard",
      "/sprzedaz/dashboard",
      "/sprzedaz/daily",
      "/sprzedaz/kontrahent",
      "/sprzedaz/compare",
      "/sprzedaz/region",
      "/sprzedaz/typow",
      "/sprzedaz/creative",
      "/sprzedaz/dane",
      "/zakupy/dashboard",
      "/zakupy/daily",
      "/zakupy/kontrahent",
      "/zakupy/region",
      "/zakupy/typow",
      "/zakupy/compare",
      "/zakupy/creative",
      "/zakupy/dane",
      "/raportonas",
      "/daily-sales",
      "/klienci",
      "/kontrahent/raport-wiarygodnosci",
      "/import",
      "/admin/faktura",
      "/admin/ustawenia",
      "/adminpanel/dashboard",
      "/adminpanel/admin-users",
      "/adminpanel/orders",
      "/adminpanel/revenue",
      "/adminpanel/invoice",
      "/adminpanel/basic-demo-accesses",
    ];

    const isProtectedRoute = protectedPaths.some((path) =>
      location.pathname.startsWith(path)
    );
    if (!isProtectedRoute) return;

    const handleLogout = async () => {
      console.log("Session expired. Logging out.");
      localStorage.clear();
      window.indexedDB && window.indexedDB.deleteDatabase("MyAppDB");
      navigate("/login");
    };

    const resetTimer = () => {
      clearTimeout(logoutTimer);
      logoutTimer = setTimeout(() => {
        handleLogout();
      }, 10 * 60 * 1000); // 10 minutes
    };

    window.addEventListener("mousemove", resetTimer);
    window.addEventListener("keydown", resetTimer);
    window.addEventListener("click", resetTimer);
    window.addEventListener("scroll", resetTimer);

    resetTimer();

    return () => {
      clearTimeout(logoutTimer);
      window.removeEventListener("mousemove", resetTimer);
      window.removeEventListener("keydown", resetTimer);
      window.removeEventListener("click", resetTimer);
      window.removeEventListener("scroll", resetTimer);
    };
  }, [navigate, location.pathname]);

  return null;
};

function App() {
  return (
    <Router>
      <GlobalStylesSelector />
      <TokenPersister />
      <SessionWatcher />
      <GtmRouteChangeTracker /> {/* 🔔 pushes pageview events on route changes */}

      <div className="App">
        <Suspense fallback={<div style={{ padding: 24 }}>Ładowanie…</div>}>
          <Routes>
            {/* Public Pages */}
            <Route path="/" element={<Home />} />
            <Route path="/partner" element={<DlaKogo />} />
            <Route path="/cennik" element={<Cennik />} />
            <Route path="/blog" element={<Blog />} />  
            <Route path="/contact" element={<Contact />} />
            <Route path="/select-license" element={<SelectLicensePage />} />
            <Route path="/order-summary" element={<OrderSummaryPage />} />
            <Route path="/success" element={<SuccessPage />} />
            <Route path="/cancel" element={<CancelPage />} />
            <Route path="/demo-login" element={<DemoAutoLogin />} />

            {/* Admin Login */}
            <Route path="/adminpanel/adminlogin" element={<AdminLogin />} />
            <Route path="/adminpanel/dashboard" element={<ProtectedRoute element={AdminPanel} />} />
            <Route path="/adminpanel/admin-users" element={<ProtectedRoute element={AdminUserList} />} />
            <Route path="/adminpanel/orders" element={<ProtectedRoute element={AdminOrdersList} />} />
            <Route path="/adminpanel/revenue" element={<ProtectedRoute element={Przychody} />} />
            <Route path="/adminpanel/invoice" element={<ProtectedRoute element={AdminInvoicesList} />} />
            <Route path="/adminpanel/settings" element={<Settings />} />
            <Route path="/adminpanel/basic-demo-accesses" element={<ProtectedRoute element={AdminBasicDemoAccesses} />} />

            {/* Auth Pages */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/register-demo" element={<RegisterDemo />} />
            <Route path="/confirm-email" element={<ConfirmEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Protected Dashboard Pages */}
            <Route path="/sprzedaz/dashboard" element={<ProtectedRoute element={SprzedazComponent} />} />
            <Route path="/sprzedaz/daily" element={<ProtectedRoute element={SprzedazDzien} />} />
            <Route path="/raportonas" element={<ProtectedRoute element={RaportONasPage} />}/>
            <Route path="/sprzedaz/kontrahent" element={<ProtectedRoute element={SprzedazMiec} />} />
            <Route path="/sprzedaz/compare" element={<ProtectedRoute element={SprzedazCompare} />} />
            <Route path="/sprzedaz/region" element={<ProtectedRoute element={SprzedazMap} />} />
            <Route path="/sprzedaz/typow" element={<ProtectedRoute element={SprzedazTypow} />} />
            <Route path="/sprzedaz/creative" element={<ProtectedRoute element={SprzedazCreative} />} />
            <Route path="/sprzedaz/dane" element={<ProtectedRoute element={SprzedazData} />} />

            <Route path="/zakupy/daily" element={<ProtectedRoute element={ZakupyDzien} />} />
            <Route path="/zakupy/kontrahent" element={<ProtectedRoute element={ZakupyKontrahent} />} />
            <Route path="/zakupy/compare" element={<ProtectedRoute element={ZakupyCompare} />} />
            <Route path="/zakupy/region" element={<ProtectedRoute element={ZakupyRegion} />} />
            <Route path="/zakupy/typow" element={<ProtectedRoute element={ZakupyTypow} />} />
            <Route path="/zakupy/creative" element={<ProtectedRoute element={ZakupyCreative} />} />
            <Route path="/zakupy/dane" element={<ProtectedRoute element={ZakupyData} />} />

            <Route path="/daily-sales" element={<ProtectedRoute element={DailySales} />} />
            <Route path="/klienci" element={<ProtectedRoute element={KlienciComponent} />} />

            {/* Kontrahent → Raporty */}
            <Route
              path="/kontrahent/raport-wiarygodnosci"
              element={
                <ProtectedRoute
                  element={() => (
                    <ImportLicenseGuard
                      component={RaportWiarygodnosciPage}
                      featureName="Raport wiarygodności kontrahenta"
                    />
                  )}
                />
              }
            />
            <Route path="/kontrahent/krs-pelny" element={<ProtectedRoute element={KrsPelnyPage} />} />
            <Route path="/kontrahent/krs-aktualny" element={<ProtectedRoute element={KrsAktualnyPage} />} />
            <Route path="/kontrahent/ceodg-aktualny"element={<ProtectedRoute element={CeodgAktualnyPage} />}/>

            <Route
              path="/import"
              element={
                <ProtectedRoute
                  element={() => (
                    <ImportLicenseGuard
                      component={ImportComponent}
                      featureName="Import danych"
                    />
                  )}
                />
              }
            />
            <Route
              path="/import/przeglad"
              element={
                <ProtectedRoute
                  element={() => (
                    <ImportLicenseGuard
                      component={ImportOverview}
                      featureName="Przegląd importów"
                    />
                  )}
                />
              }
            />
            <Route
              path="/import/historia"
              element={
                <ProtectedRoute
                  element={() => (
                    <ImportLicenseGuard
                      component={ImportHistory}
                      featureName="Historia importów"
                    />
                  )}
                />
              }
            />
            <Route
              path="/import/analiza-finansowa"
              element={<ProtectedRoute element={FinancialAnalysisPage} />}
            />

            <Route
              path="/admin/faktura"
              element={
                <ProtectedRoute
                  element={() => (
                    <ImportLicenseGuard
                      component={Faktura}
                      featureName="Faktury"
                    />
                  )}
                />
              }
            />
            <Route
              path="/admin/ustawenia"
              element={
                <ProtectedRoute
                  element={() => (
                    <ImportLicenseGuard
                      component={InvoicingSettings}
                      featureName="Ustawienia"
                    />
                  )}
                />
              }
            />
            <Route
              path="/admin/subskrypcja"
              element={
                <ProtectedRoute
                  element={() => (
                    <ImportLicenseGuard
                      component={Subscription}
                      featureName="Subskrypcja"
                    />
                  )}
                />
              }
            />

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </div>
    </Router>
  );
}

export default App;
